import { hasUnresolvedReports } from "@/react/components/CommentsCompass/helpers";
import { useCommentScrollTarget } from "@/react/components/CommentsCompass/Provider/ScrollContext";
import { cssAnchorProps } from "@/react/helpers/cssAnchorHelpers";
import { classNames } from "@/react/helpers/twMergeWithCN";
import type { Comment as CommentType } from "@/react/types/Comment";

import { ActionMenu } from "./ActionMenu";
import { Author } from "./Author";
import { Avatar } from "./Avatar";
import { Body } from "./Body";
import { EngagementActions } from "./EngagementActions";
import { ModerationBanner } from "./ModerationBanner";
import { useCommentPermissions } from "./useCommentPermissions";
import { useEngagementActionsVisibility } from "./useEngagementActionsVisibility";

interface CommentProps {
  comment: CommentType;
  indented?: boolean;
}

export const Comment = ({ comment, indented = false }: CommentProps) => {
  const { canModerate } = useCommentPermissions(comment);
  const { canShowLikeAction, canShowReplyAction } =
    useEngagementActionsVisibility();
  const scrollRef = useCommentScrollTarget(comment.id);

  const shouldShowModerationBanner =
    canModerate && hasUnresolvedReports(comment);
  const shouldShowEngagementActions = canShowLikeAction || canShowReplyAction;

  return (
    <article
      ref={scrollRef}
      className="col-span-4 grid grid-cols-subgrid items-start pt-2"
      {...cssAnchorProps({ anchor: "comment" })}
    >
      <div
        className={classNames(
          "row-span-2 flex",
          indented ? "col-start-2" : "col-start-1",
        )}
      >
        <Avatar comment={comment} />
      </div>
      <div
        className={classNames(
          "flex flex-col gap-0.5",
          indented ? "col-start-3" : "col-start-2 col-span-2",
        )}
      >
        <Author comment={comment} />
      </div>
      <div className="col-start-4">
        <ActionMenu comment={comment} />
      </div>
      <div
        className={classNames(
          "mt-0.5 mb-2",
          indented ? "col-start-3 col-span-2" : "col-start-2 col-span-3",
        )}
      >
        <div className="flex flex-col gap-2">
          <Body comment={comment} />
          {shouldShowModerationBanner && <ModerationBanner comment={comment} />}
        </div>
      </div>

      {shouldShowEngagementActions && (
        <div
          className={classNames(
            "mt-0.5",
            indented ? "col-start-3 col-span-2" : "col-start-2 col-span-3",
          )}
        >
          <EngagementActions comment={comment} />
        </div>
      )}
    </article>
  );
};
